/**
 * @lines98/client/lib/achievement-seen-badge — ЧИСТАЯ логика точки-индикатора «есть новые
 * ачивки» на нав-пункте «Профиль» + per-player seen-set открытых ачивок (тикет
 * fe-achievement-result-window, AC-8/19; owner-decisions §«Оркестратор-резолюции» п.1).
 *
 * ЗАЧЕМ / WHY THIS EXISTS (code-structure §3 «правило-предикат → модуль, один источник»;
 * owner-directive 2026-05-30). По образцу `lib/gnome-unlock-badge` (снимок-ствол
 * `createScreenSnapshot`, НЕ ad-hoc localStorage), НО питается УЖЕ существующим server-truth
 * open-set ачивок `snapshot.progression.achievementIds` (его читает Профиль) — НОВОЕ
 * серверное поле / монотонный счётчик НЕ заводится (резолюция оркестратора п.1).
 *
 * Точка = «open-set (server-truth) ⊋ seen-set (локальный per-player снимок)». Поэтому
 * предикат — НА МНОЖЕСТВАХ ID (а не на двух числах, как gnome-unlock-badge): сравнение по
 * списку id — именно тот приём, что мандатировал оркестратор для ачивок (ротации id нет в
 * рамках одного игрока — open-set растёт монотонно). Сброс seen-set при заходе в Профиль
 * (seen := open) → точка гаснет (AC-8).
 *
 * Точка — чистый UX-индикатор «загляни»; на мету/добычу/ранг НЕ влияет (display-only).
 *
 * SCOPE: предикат `hasUnseenAchievements` + per-player seen-set (load/save). NOT IN SCOPE:
 * рендер dot'а (рейл-API `nav-rail.setItemBadge` + `.nav-item-badge--dot` CSS); источник
 * open-set (`lib/meta-snapshot.progression.achievementIds`); каталог ачивок.
 *
 * Cross-references:
 *   - lib/gnome-unlock-badge.ts (ЭТАЛОН-структура: снимок-ствол + предикат + load/save).
 *   - lib/screen-snapshot.ts (ствол per-player снимка — изоляция/версия/persistence).
 *   - lib/meta-snapshot.ts (`progression.achievementIds` — server-truth open-set, источник).
 *   - lib/nav-rail.ts (`setItemBadge('profile', …)` — dot через рейл-API; consumer).
 *   - screens/profile-screen.ts (сброс seen := open при mount, AC-8).
 */

import { createScreenSnapshot } from './screen-snapshot.js';

/**
 * Есть ли непросмотренные ачивки (точка видна?) — существует id ∈ open, которого НЕТ в seen
 * (open ⊋ seen по непустому диффу, AC-8). Сравнение МНОЖЕСТВ (порядок/дубли не влияют).
 * Defensive: не-итерабельный / null / мусор → `false` (точка не светится на мусоре). Чистая
 * функция (юнит-тестируема без DOM).
 *
 * @param open — server-truth open-set ачивок (`snapshot.progression.achievementIds`).
 * @param seen — последний подтверждённый заходом в Профиль seen-set (per-player).
 */
export function hasUnseenAchievements(open: Iterable<string>, seen: Iterable<string>): boolean {
  if (!isIterable(open) || !isIterable(seen)) return false;
  const seenSet = new Set<string>();
  for (const id of seen) {
    if (typeof id === 'string' && id.length > 0) seenSet.add(id);
  }
  for (const id of open) {
    if (typeof id === 'string' && id.length > 0 && !seenSet.has(id)) return true;
  }
  return false;
}

/** Итерабелен ли вход (защита от числа/null/undefined вместо коллекции). */
function isIterable(value: unknown): value is Iterable<string> {
  return value != null && typeof (value as { [Symbol.iterator]?: unknown })[Symbol.iterator] === 'function';
}

// =============================================================================
// seen-set открытых ачивок — per-player (ветка снимка-ствола, R8/AC-19)
// =============================================================================

/** Префикс ключа seen-set; полный ключ = `${PREFIX}${playerId}` (изоляция по игроку). */
const KEY_PREFIX = 'lines98.ach.seen.v1.';
/** Версия payload — seen = массив строковых id (несовпадение → отбрасываем). */
const SCHEMA_VERSION = 1;

/** Ветка снимка: per-player seen-set открытых ачивок (string[]). Persistence/изоляция — в стволе. */
const store = createScreenSnapshot<string[]>({
  keyPrefix: KEY_PREFIX,
  version: SCHEMA_VERSION,
  isValid: (v): v is string[] => Array.isArray(v) && v.every((s) => typeof s === 'string'),
});

/**
 * Прочитать seen-set ТЕКУЩЕГО игрока СИНХРОННО (для рейл-точки при mount). Нет снимка /
 * гость / битый → `[]` (свежая сессия: любой открытый id даёт точку, AC-8).
 */
export function loadSeenAchievements(): string[] {
  return store.load() ?? [];
}

/**
 * Записать seen := open ТЕКУЩЕГО игрока (сброс точки при заходе в Профиль, AC-8). No-op для
 * гостя (нет ключа). Defensive-persistence в стволе. Дедуп + фильтр мусора (хранится чистый
 * набор валидных id).
 *
 * @param open — текущий server-truth open-set ачивок (его подтверждает заход в Профиль).
 */
export function saveSeenAchievements(open: Iterable<string>): void {
  if (!isIterable(open)) return;
  const set = new Set<string>();
  for (const id of open) {
    if (typeof id === 'string' && id.length > 0) set.add(id);
  }
  store.save([...set]);
}
